/**
 * cas-1d88 (T7) — shared decision helper for /dev/** access gating.
 *
 * The Nuxt global route middleware at `app/src/middleware/`
 * uses this predicate on both the SSR and client navigation paths.
 * Keeping the predicate pure (no Nuxt or h3 imports) lets us unit-test
 * it without a Nuxt runtime and prevents the two execution paths from
 * drifting apart.
 *
 * The override is intentionally explicit in public runtime config:
 * setting it is the operator's declaration that this deployment may
 * expose demo pages. Staging is independently allowed from the public
 * API base so a production-mode staging build cannot accidentally use
 * the production default-deny branch.
 */

import { stripLocalePrefix } from './public-audit-path';

// Internal — not exported because no external consumer references it.
type DevPagesFlagValue = string | boolean | undefined | null;

type DevPagesDeploymentOptions = {
  apiBase?: string | null;
  host?: string | null;
};

/**
 * Strip `?query` and `#fragment` from a route location so the matcher works
 * on just the pathname. A route location can include either suffix, and
 * `/dev?cache=bust` would otherwise bypass the prefix match.
 */
function pathnameOf(path: string): string {
  const queryIdx = path.indexOf('?');
  const fragIdx = path.indexOf('#');
  let end = path.length;
  if (queryIdx !== -1) end = Math.min(end, queryIdx);
  if (fragIdx !== -1) end = Math.min(end, fragIdx);
  return path.slice(0, end);
}

/**
 * cas-4b75: reduce a path to the spelling the router actually resolves, so every
 * alias of a pages/dev route is judged the same way. vue-router matches
 * case-insensitively and decodes percent-escapes, and i18n's
 * `prefix_except_default` strategy gives every page an `/es/...` twin. On prod,
 * `/es/dev/test-auth` and `/Dev/test-auth` both rendered the demo pages because
 * the gate only compared the raw path with `/dev`.
 */
function canonicalPathname(path: string): string {
  let pathname = pathnameOf(path);
  try {
    pathname = decodeURIComponent(pathname);
  } catch {
    // A malformed escape: judge the raw path rather than throwing in middleware.
  }
  pathname = pathname.toLowerCase().replace(/\/{2,}/g, '/');
  return stripLocalePrefix(pathname);
}

/**
 * Match the /dev pages path-set, in every locale and spelling:
 *   - exact "/dev" (also "/es/dev")
 *   - any path starting with "/dev/" (also "/es/dev/...")
 *
 * Strips `?query`/`#fragment`, decodes escapes, lower-cases, collapses
 * repeated slashes and removes a non-default locale prefix first, so
 * `/dev?x=1`, `/es/dev/foo`, `/Dev/foo` and `/%64ev/foo` all match.
 *
 * IMPORTANT: must NOT match prefix collisions like "/develop" or
 * "/devices" — those are real product paths, blocking them would be a
 * regression.
 */
export function isDevPagesPath(path: string): boolean {
  const pathname = canonicalPathname(path);
  return pathname === '/dev' || pathname.startsWith('/dev/');
}

function hostnameOf(value: string | null | undefined): string | null {
  if (!value) return null;

  try {
    return new URL(value.includes('://') ? value : `https://${value}`).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function isProductionHost(host: string | null): boolean {
  return host === 'gabber.studio' || host === 'www.gabber.studio';
}

function isStagingApiBase(apiBase: string | null): boolean {
  const hostname = hostnameOf(apiBase);
  return hostname !== null && /(^|[-.])staging([-.]|$)/i.test(hostname);
}

function isStagingHost(host: string | null): boolean {
  return host === 'staging.gabber.studio' || host?.endsWith('.vercel.app') === true;
}

/**
 * Identify staging using the public API base already shared by SSR and the
 * browser. Host detection is the fallback for Vercel previews and for a
 * deployment where the API base was not populated. The canonical production
 * host always wins, so a misconfigured production API base cannot open /dev/**.
 */
export function isStagingDeployment(options: DevPagesDeploymentOptions): boolean {
  const host = hostnameOf(options.host);
  if (isProductionHost(host)) return false;
  if (isStagingApiBase(options.apiBase ?? null)) return true;
  return isStagingHost(host);
}

/**
 * Local `nuxt dev` / localhost. Used by the Audit V3 → Create exit so the
 * same path can be walked without a staging deploy. Production hosts never
 * count, even if `isDev` is somehow true in a misbuilt bundle.
 */
export function isLocalDevelopment(options: { host?: string | null; isDev?: boolean }): boolean {
  const host = hostnameOf(options.host);
  if (isProductionHost(host)) return false;
  if (options.isDev) return true;
  return host === 'localhost' || host === '127.0.0.1';
}

/**
 * Decide whether a request to `path` should be blocked (404) given the
 * current build mode and the override-flag value.
 *
 * Returns `true` => caller should 404 the request.
 * Returns `false` => caller should let the request through.
 *
 * Rules (in priority order):
 *   1. Non-dev paths are never blocked (neither the URL nor any matched
 *      route record is a pages/dev route, in any locale or spelling).
 *   2. In a dev-mode build (`nuxt dev`), nothing is blocked — the
 *      whole point of /dev/** is to be reachable during local dev.
 *   3. In staging, allow the pages regardless of the flag.
 *   4. Otherwise, allow only when the override flag is *exactly* the
 *      string "true" or the boolean `true`. Strict match is deliberate:
 *      'TRUE', '1', and 'yes' all fall through to block.
 *   5. Anything else (undefined, '', 'false', '0', 'TRUE', etc.) blocks.
 */
export function shouldBlockDevPages(opts: {
  path: string;
  /**
   * cas-4b75: the `path`s of the route records the router matched (`to.matched`).
   * These are the canonical page paths (`/dev/test-auth`, `/es/dev/test-auth`),
   * so a dev page is caught whatever spelling the URL used.
   */
  matchedPaths?: readonly string[];
  isDev: boolean;
  flagValue: DevPagesFlagValue;
  apiBase?: string | null;
  host?: string | null;
}): boolean {
  const paths = [opts.path, ...(opts.matchedPaths ?? [])];
  if (!paths.some(isDevPagesPath)) return false;
  if (opts.isDev) return false;
  if (isStagingDeployment({ apiBase: opts.apiBase, host: opts.host })) return false;
  if (opts.flagValue === true || opts.flagValue === 'true') return false;
  return true;
}
